/**
 * Legacy URL Redirect — Constants & Pure Functions
 *
 * Maps old Demandware/Salesforce Commerce Cloud product URLs to Shopify equivalents.
 * All functions are pure (no side-effects) for easy testing.
 *
 * @module legacy-url-redirect/constants
 */

/**
 * Matches old Demandware product URLs and captures the locale segment.
 *
 * Example path: /on/demandware.store/Sites-deciem-Site/en_CA/Product-Show
 * Also matches: /on/demandware.store/Sites-deciem-us-Site/en_US/Product-Show
 * Capture group 1: locale (e.g. "en_CA")
 */
export const DEMANDWARE_PATH_PATTERN =
  /^\/on\/demandware\.store\/Sites-deciem-(?:\w+-)?Site\/([a-zA-Z]{2}_[a-zA-Z]{2})\/Product-Show/;

/**
 * Maps Demandware product IDs (pid) to Shopify product handles.
 * Add new entries here as needed — no other code changes required.
 */
export const PRODUCT_MAP: Record<string, string> = {
  100703: 'oud-amber-hand-body-wash',
  100704: 'chalk-hand-body-wash',
  100705: 'broadleaf-forest-hand-body-wash',
  100709: 'biomimetic-body-serum',
  100710: 'broadleaf-forest-hand-body-powder-wash',
  100711: 'oud-amber-hand-body-powder-wash',
  100712: 'chalk-hand-body-powder-wash',
  100746: 'broadleaf-forest-hand-body-water-cream',
  100747: 'chalk-hand-body-water-cream',
  100748: 'oud-amber-hand-body-water-cream',
  100759: 'helium-hand-cream',
  769915040401: 'oud-amber-hand-body-powder-wash',
};

/**
 * Converts a Demandware locale string to a Shopify-style locale.
 * @param {string} raw - e.g. "en_CA"
 * @returns {string} e.g. "en-ca"
 */
export function convertLocale(raw: string): string {
  return raw.replace('_', '-').toLowerCase();
}

/**
 * Extracts the locale and product ID from a Demandware URL.
 * @param {string} pathname - window.location.pathname
 * @param {string} search   - window.location.search
 * @returns {{ locale: string, pid: string } | null}
 */
export function extractDemandwareParams(
  pathname: string,
  search: string,
): { locale: string; pid: string } | null {
  const match = pathname.match(DEMANDWARE_PATH_PATTERN);
  if (!match) return null;

  const params = new URLSearchParams(search);
  const pid = params.get('pid');
  if (!pid) return null;

  return { locale: match[1], pid };
}

/**
 * Builds a Shopify product URL.
 * @param {string} locale - e.g. "en-ca"
 * @param {string} handle - e.g. "chalk-hand-body-powder-wash"
 * @returns {string} e.g. "/en-ca/products/chalk-hand-body-powder-wash"
 */
export function buildShopifyUrl(locale: string, handle: string): string {
  return `/${locale}/products/${handle}`;
}
